import { createRouter, createWebHistory } from 'vue-router';
import { listRouter } from './autoRouter';
import { ElMessage } from 'element-plus';
import { LocaleTypeEnum } from '@/store/locale';
import { trackPageView } from '@/utils/baiduAnalytics';
import type { EventsTab } from '@/store/events';
import { getUserInfo } from '@/apis/user';
import { getCookie } from '@/utils/cookies';
import { goLogin } from '@/utils/commonFun';

// 路由 lang 参数映射
export const LANG_MAP = {
    cn: LocaleTypeEnum.Chinese,
    en: LocaleTypeEnum.English,
} as const;

export const LOCALE_TO_LANG = {
    [LocaleTypeEnum.Chinese]: 'cn',
    [LocaleTypeEnum.English]: 'en',
} as const;

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL || '/'),
    routes: listRouter,
    scrollBehavior: (to, from, savedPosition) => {
        return { left: 0, top: 0 }; // 强制滚动到顶部
    },
});

let isFirstReqestUserInfo = true;
// 需要登录的页面
const loginPage = ['/OnlineLab'];
router.beforeEach(async (to, from, next) => {
    const { setUserInfo } = useStore('user');
    const token = getCookie('baai-data-admin-token-key');
    if (!token && loginPage.includes(to.path)) {
        next(false);
        goLogin();
        return;
    }
    if (!loginPage.includes(to.path)) {
        if (isFirstReqestUserInfo && getCookie('baai-data-admin-token-key')) {
            isFirstReqestUserInfo = false;
            getUserInfo(false).then(res => {
                res && setUserInfo(res);
            });
        }
        next();
        return;
    }
    if (isFirstReqestUserInfo) {
        getUserInfo().then(userInfo => {
            userInfo && setUserInfo(userInfo);
            isFirstReqestUserInfo = false;
        }); // 不显示错误提示，不自动跳转登录
    }

    next();
});

router.beforeEach(async (to, from, next) => {
    const { getTabFromQuery, setActiveTab, activeTab } = useStore('events');
    // 处理 Events 页面的 tab 参数（仅在 /events 路由下处理）
    if (to.path === '/events' || to.path.startsWith('/events')) {
        // 如果路由参数中有 tab，使用路由参数的值并更新 store
        if (to.query.tab || to.query.activeTab) {
            const tabFromQuery = getTabFromQuery(to.query);
            setActiveTab(tabFromQuery);
        } else {
            // 如果路由参数中没有 tab，优先使用 store 中保存的值（从详情页返回时保留上次选中的 tab）
            const savedTab = activeTab.value;
            const tabToUse: EventsTab = savedTab && (savedTab === 'activity' || savedTab === 'competition') ? savedTab : 'activity';
            setActiveTab(tabToUse);
            // 将 tab 值同步到路由参数中
            next({
                ...to,
                query: { ...to.query, tab: tabToUse },
                replace: true,   // 这里为什么要replace
            });
            return;
        }
    }
    next();
});

// 路由切换后，上报百度统计
router.afterEach((to, from) => {
    // 等待下一个 tick，确保页面已经渲染和百度统计脚本已加载
    setTimeout(() => {
        // 获取页面路径，移除 lang 参数（因为 lang 是用于语言切换的，不应该影响统计）
        const queryWithoutLang = Object.entries(to.query)
            .filter(([key]) => key !== 'lang')
            .reduce(
                (acc, [key, value]) => {
                    acc[key] = value;
                    return acc;
                },
                {} as Record<string, any>
            );

        // 构建路径（包含除 lang 外的其他查询参数）
        const pathWithoutLang =
            Object.keys(queryWithoutLang).length > 0
                ? `${to.path}?${Object.entries(queryWithoutLang)
                    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
                    .join('&')}`
                : to.path;

        // 获取页面标题
        const pageTitle: string =
            (to.meta?.title as string) || (typeof to.name === 'string' ? to.name : String(to.name || '')) || to.path || '未知页面';

        // 上报百度统计（函数内部会处理脚本未加载的情况）
        trackPageView(pathWithoutLang, pageTitle);
    }, 300); // 增加延迟，确保百度统计脚本有足够时间加载
});

router.onError((err, to, from) => {
    if (err) {
        ElMessage({ message: '数据更新，刷新页面获取最新数据' });
    }
    console.log('error', err, to, from);
});
export default router;
